import { createContext, useContext, useState } from "react";
import PurchaserClient from "../clients/PurchaserClient";
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { queryClient } from "../clients/ReactQueryClient";
import { useNavigate } from "react-router";
import type { User } from "../clients/Purchaser/Auth";

export const UserContext = createContext<{
    /** The current user, or null if not logged in */
    user: User | null;
    /** A function to refetch the user data */
    refetchUser: () => Promise<any>;
    /** The status code of the last user fetch */
    statusCode: number;
    /** True if the user is not logged in */
    isAnon: boolean;
    /** True if we are offline and can't hit the API */
    isOffline: boolean;
    /** Function to log out the current user */
    logout: () => Promise<void>;
}>(null!);

interface UserProviderProps {
    children: React.ReactNode;
}

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }: UserProviderProps) => {
    const posthog = usePostHog();
    const [refetchEnabled, setRefetchEnabled] = useState(true);
    // the interval at which the user is refetched
    // this changes based on the current auth state.
    // - if the user is offline, we refetch every 5 seconds to try to reestablish connection
    // - if the user logged in, we refetch every 5 minutes to keep the user data up to date
    // - if the user is logged out, we don't refetch at all until they log back in
    const [refetchInterval, setRefetchInterval] = useState<number>(300000);

    const userQuery = useQuery<{ statusCode: number; user?: User | null } | null>({
        queryKey: ["user"],
        queryFn: () =>
            PurchaserClient.getCurrentUser()
                .then(res => {
                    // User is logged in
                    if (res && res.user) {
                        // Set the user in PostHog
                        posthog.identify(
                            res.user.id.toString(),
                            {
                                name: `${res.user.first_name} ${res.user.last_name}`,
                                teamId: res.user.lastTeam.id
                            },
                            { email: res.user.email }
                        );
                    }

                    if (res.statusCode === 401) {
                        // unauthenticated user - no need to refetch at all
                        setRefetchEnabled(false);
                    } else if (res.statusCode === 0) {
                        // offline user - refetch every 5 seconds to try to reestablish connection
                        setRefetchEnabled(true);
                        setRefetchInterval(5000);
                    } else {
                        // normal scenario - refetch every 5 minutes to keep the user data up to date
                        setRefetchEnabled(true);
                        setRefetchInterval(300000);
                    }
                    return res;
                })
                .catch(err => {
                    console.error("Failed to fetch user", err);
                    // Eat the error and return null
                    return { statusCode: 0, user: null };
                }),
        initialData: null,
        refetchIntervalInBackground: true,
        refetchInterval: refetchInterval,
        enabled: refetchEnabled
    });

    const user = userQuery.data?.user || null;

    const logout = async () => {
        // Remove the token from local storage
        localStorage.removeItem("token");
        localStorage.removeItem("compliance-audit");

        // Invalidate the token on the server
        await userQuery.refetch();

        // Clear the query cache in react query
        queryClient.clear();
    };

    return (
        <UserContext.Provider
            value={{
                user: user,
                refetchUser: userQuery.refetch,
                statusCode: userQuery.data?.statusCode || 0,
                isAnon: userQuery.data?.statusCode === 401,
                isOffline: userQuery.data?.statusCode === 0,
                logout
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
